// ============================================================================
// hooks/liveStreamManagment/useAudioLevel.ts
//
// v1.0 — Ponts React ↔ audioLevelStore.
//
// `useAudioLevelEffect` est le pont normal : il donne au composant une fonction
// appelée à chaque frame, hors cycle de rendu React. Le composant s'en sert pour
// écrire directement dans le DOM (largeur de barre, couleur, texte). Il ne
// provoque JAMAIS de rendu — c'est tout l'intérêt.
//
// `useMeteredSourceIds` est l'exception assumée : il re-rend, mais seulement
// quand l'ensemble des sources mesurées change (connexion / retrait d'une
// source), pas au rythme des niveaux.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import {
  AudioLevelStore,
  audioLevelStore,
  SourceLevels,
} from '../../dataTypes/liveStreamManagment/audioPipeline/audioLevelStore';

/**
 * Abonne le composant aux niveaux de `sourceId` et appelle `apply` à chaque
 * frame, ainsi qu'une fois immédiatement avec la valeur courante.
 *
 * `apply` est stockée dans une ref : elle peut être une lambda inline sans
 * provoquer de réabonnement. En revanche elle est appelée hors rendu, donc elle
 * ne doit toucher qu'au DOM (via des refs) — jamais à un `setState`.
 *
 * `store` permet de viser `intercomLevelStore` au lieu du store du deck.
 */
export function useAudioLevelEffect(
  sourceId: string | null | undefined,
  apply: (levels: SourceLevels) => void,
  store: AudioLevelStore = audioLevelStore,
): void {
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!sourceId) return;
    const handler = (levels: SourceLevels) => applyRef.current(levels);
    handler(store.get(sourceId));
    return store.subscribe(sourceId, handler);
  }, [sourceId, store]);
}

/**
 * Liste des sources actuellement mesurées. Ne change — et ne re-rend — que
 * lorsqu'une source est connectée ou retirée.
 *
 * Sert de signal de rafraîchissement pour ce qui dépend de l'AudioSourceRegistry,
 * qui n'est pas réactif : c'est exactement au moment où l'audio d'un slot vient
 * d'être branché que ces dérivés doivent être réévalués.
 */
export function useMeteredSourceIds(): string[] {
  const [ids, setIds] = useState<string[]>(() => audioLevelStore.getSourceIds());

  useEffect(() => {
    setIds(audioLevelStore.getSourceIds());
    return audioLevelStore.subscribeSourceSet(setIds);
  }, []);

  return ids;
}
